import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DisplayFormat, ProfessionRuleInput, ProfessionRulePatch } from "@juro/shared";
import { tenantProfessionsApi, type ProfessionRule, type TenantProfession } from "../../api/tenantProfessionsApi.js";
import { Button } from "../ui/Button.js";
import type { IssuerMasterSaveInput } from "../../hooks/useIssuerMaster.js";
import type { IssuerRef } from "../../lib/issuerMaster.js";

/** 値が自動解決される field_key（一覧に出さない） */
const AUTO_FIELD_KEYS = new Set(["holder_signature", "invoice_registration_number"]);

const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

type NewItem = { field_key: string; label: string; value: string; fromTemplate: boolean };

/**
 * 発行者情報（業務マスタ）の値のみを編集するフォーム。
 * PDF 上の表示位置・書式はテンプレート側の責務なので、ここでは扱わない。
 *
 * 資格者の氏名・登録番号・適格請求書発行事業者番号は資格者名簿
 * （tenant_profession_holders）を真実の源とするため、ここでは「既定の資格者」を
 * 選ぶだけで、氏名・番号自体の編集は名簿側（children で差し込まれる
 * ProfessionHolderRoster）で行う（docs/design/user-professional-separation.md §8.3）。
 */
export function IssuerValuesForm({
  profession,
  saving,
  referencedKeys,
  onSave,
  onDeleteRule,
  onDeleteProfession,
  deletingProfession,
  roster,
  children,
}: {
  profession: TenantProfession;
  saving: boolean;
  /** テンプレートが参照している発行者情報キー（未入力警告の対象） */
  referencedKeys?: IssuerRef[];
  onSave: (input: IssuerMasterSaveInput) => Promise<unknown>;
  onDeleteRule: (rule: ProfessionRule) => void;
  /** この資格自体を削除する（カスタム資格のみ。組み込み資格は渡さない＝ボタンを出さない） */
  onDeleteProfession?: () => void;
  deletingProfession?: boolean;
  /**
   * 資格者名簿（ProfessionHolderRoster）。「既定の資格者」ドロップダウンの
   * 直下に差し込む（操作の原因と操作先を近くに置く。§8.2-4）。
   */
  roster?: React.ReactNode;
  /** カード下部に差し込む追加コンテンツ（例: この資格の書類種別） */
  children?: React.ReactNode;
}) {
  const refs = useMemo(() => new Set<IssuerRef>(referencedKeys ?? []), [referencedKeys]);
  // サーバー値はそのまま参照し、ユーザー編集分だけを overrides に持つ（保存後の refetch と競合しない）
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [newItems, setNewItems] = useState<NewItem[]>([]);

  const regRule = profession.rules.find((rule) => rule.field_key === "registration_number") ?? null;
  const bizRule = profession.rules.find((rule) => rule.field_key === "business_label") ?? null;
  const fixedRuleIds = new Set(
    [
      ...(regRule ? [regRule.id] : []),
      ...(bizRule ? [bizRule.id] : []),
      ...profession.rules.filter((rule) => AUTO_FIELD_KEYS.has(rule.field_key)).map((rule) => rule.id),
    ],
  );
  const customRules = profession.rules.filter((rule) => !fixedRuleIds.has(rule.id));

  // テンプレートが参照しているのに未登録のカスタムキーは、入力行として自動で提示する
  useEffect(() => {
    const existing = new Set(profession.rules.map((rule) => rule.field_key));
    const missing = [...refs]
      .filter((ref) => ref.startsWith("rules."))
      .map((ref) => ref.slice("rules.".length))
      .filter((key) => !existing.has(key));
    if (missing.length === 0) return;
    setNewItems((current) => {
      const known = new Set(current.map((item) => item.field_key));
      const added = missing.filter((key) => !known.has(key));
      if (added.length === 0) return current;
      return [...current, ...added.map((key) => ({ field_key: key, label: "", value: "", fromTemplate: true }))];
    });
  }, [refs, profession.rules]);

  // 「既定の資格者」はこの資格の名簿（ProfessionHolderRoster と同じクエリキーで
  // キャッシュ共有）から選ぶ（profession-master-unification.md §9 /
  // user-professional-separation.md §8.3）。
  const holdersQuery = useQuery({
    queryKey: ["tenant-profession-holders", profession.profession],
    queryFn: () => tenantProfessionsApi.listHolders(profession.profession),
  });
  const holders = holdersQuery.data?.data ?? [];

  const holderId = overrides["head.holder"] ?? profession.holder_id ?? "";
  const qualificationLabel = overrides["head.label"] ?? profession.qualification_label;
  const selectedHolder = holders.find((h) => h.id === holderId) ?? null;

  const regValue = overrides["reg"] ?? regRule?.value ?? "";
  const bizValue = overrides["biz"] ?? bizRule?.value ?? "";
  const showBiz = profession.profession === "surveyor" || !!bizRule || refs.has("business_label");

  const ruleValue = (rule: ProfessionRule) => overrides[`rule.${rule.id}.value`] ?? rule.value ?? "";
  const ruleLabel = (rule: ProfessionRule) => overrides[`rule.${rule.id}.label`] ?? rule.label;
  const setOverride = (key: string, value: string) => setOverrides((current) => ({ ...current, [key]: value }));

  function makeRuleInput(fieldKey: string, label: string, value: string, format: DisplayFormat = "label_colon_value"): ProfessionRuleInput {
    return {
      profession: profession.profession,
      field_key: fieldKey,
      label,
      value: value.trim() || null,
      applies_to_doc_types: null,
      display_position: "header_right",
      display_format: format,
      display_order: 50,
    };
  }

  const saveInput = useMemo<IssuerMasterSaveInput>(() => {
    const updates: { ruleId: string; patch: ProfessionRulePatch }[] = [];
    const creates: ProfessionRuleInput[] = [];

    if (regRule && regValue !== (regRule.value ?? "")) updates.push({ ruleId: regRule.id, patch: { value: regValue.trim() || null } });
    if (!regRule && regValue.trim()) creates.push(makeRuleInput("registration_number", "登録番号", regValue));
    if (bizRule && bizValue !== (bizRule.value ?? "")) updates.push({ ruleId: bizRule.id, patch: { value: bizValue.trim() || null } });
    if (!bizRule && bizValue.trim()) creates.push(makeRuleInput("business_label", "業務表示", bizValue, "value_only"));

    for (const rule of customRules) {
      const patch: ProfessionRulePatch = {};
      if (ruleValue(rule) !== (rule.value ?? "")) patch.value = ruleValue(rule).trim() || null;
      if (!rule.is_builtin && ruleLabel(rule).trim() && ruleLabel(rule) !== rule.label) patch.label = ruleLabel(rule).trim();
      if (Object.keys(patch).length > 0) updates.push({ ruleId: rule.id, patch });
    }

    for (const item of newItems) {
      const key = item.field_key.trim();
      if (!FIELD_KEY_PATTERN.test(key)) continue;
      if (!item.label.trim() && !item.value.trim()) continue;
      creates.push(makeRuleInput(key, item.label.trim() || key, item.value));
    }

    const headChanged =
      qualificationLabel.trim() !== profession.qualification_label ||
      (holderId || null) !== (profession.holder_id ?? null);

    return {
      profession: profession.profession,
      head: headChanged && qualificationLabel.trim()
        ? { qualification_label: qualificationLabel.trim(), holder_id: holderId || null, display_order: profession.display_order }
        : undefined,
      updates,
      creates,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profession, holders, overrides, newItems, regRule, bizRule, regValue, bizValue, qualificationLabel, holderId]);

  const dirty = !!saveInput.head || saveInput.updates.length > 0 || saveInput.creates.length > 0;

  async function handleSave() {
    try {
      await onSave(saveInput);
      setOverrides({});
      setNewItems([]);
    } catch {
      // 失敗時（トースト表示済み）は編集内容を保持する
    }
  }

  function handleDeleteRule(rule: ProfessionRule) {
    if (confirm(`「${rule.label}」を削除しますか？PDFに印字されなくなります。`)) onDeleteRule(rule);
  }

  function handleDeleteProfession() {
    if (!onDeleteProfession) return;
    if (confirm(`「${profession.qualification_label}」を削除しますか？登録済みの資格者情報も見えなくなります。`)) {
      onDeleteProfession();
    }
  }

  function addItem() {
    const used = new Set([...profession.rules.map((rule) => rule.field_key), ...newItems.map((item) => item.field_key)]);
    let n = 1;
    while (used.has(`item_${n}`)) n += 1;
    setNewItems((current) => [...current, { field_key: `item_${n}`, label: "", value: "", fromTemplate: false }]);
  }

  function updateNewItem(index: number, patch: Partial<NewItem>) {
    setNewItems((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  const warnHolder = refs.has("name") && !holderId;
  const warnInvoiceReg = refs.has("invoice_registration_number") && !selectedHolder?.invoice_registration_number;
  const warnReg = refs.has("registration_number") && !regValue.trim();
  const warnBiz = refs.has("business_label") && !bizValue.trim();

  return (
    <section style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 15, color: "#0f172a", fontWeight: 900 }}>{qualificationLabel || profession.qualification_label}</h2>
          {profession.is_builtin ? (
            <span style={{
              fontSize: 10, fontWeight: 700, color: "#64748b", background: "#f8fafc",
              border: "1px solid #e2e8f0", borderRadius: 99, padding: "1px 7px",
            }}>
              組み込み
            </span>
          ) : (
            <span style={{
              fontSize: 10, fontWeight: 700, color: "#0f766e", background: "#f0fdfa",
              border: "1px solid #99f6e4", borderRadius: 99, padding: "1px 7px",
            }}>
              カスタム
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {onDeleteProfession && (
            <Button
              variant="ghost"
              style={{ color: "#dc2626" }}
              disabled={deletingProfession}
              onClick={handleDeleteProfession}
            >
              資格を削除
            </Button>
          )}
          {/*
            このボタンの対象は「資格名・既定の資格者・登録番号・追加項目」だけで、
            資格者名簿（roster）の追加・編集は含まない（名簿は行単位で即時保存する）。
            以前はラベルが単に「保存」だったため、名簿に氏名を入力した利用者が
            無効のままのこのボタンを押そうとして詰まった。スコープをラベルに出し、
            無効な理由を title で伝える（設計書 §8.2-1・§8.2-3）。
          */}
          <Button
            variant="primary"
            disabled={saving || !dirty}
            title={dirty ? undefined : "変更がありません（資格者の追加・編集は下の名簿でその場で保存されます）"}
            onClick={handleSave}
          >
            {saving ? "保存中..." : "設定を保存"}
          </Button>
        </div>
      </div>

      <div style={{ padding: 16, display: "grid", gridTemplateColumns: "150px minmax(220px, 1fr)", gap: "12px 14px", alignItems: "start", maxWidth: 720 }}>
        <label style={labelStyle}>既定の資格者</label>
        <div>
          <select
            style={inputStyle}
            value={holderId}
            onChange={(event) => setOverride("head.holder", event.target.value)}
          >
            <option value="">未設定</option>
            {holders.map((h) => (
              <option key={h.id} value={h.id}>{h.name}</option>
            ))}
          </select>
          {warnHolder && <div style={warnStyle}>テンプレートが資格者氏名を参照しています。既定の資格者を選択してください。</div>}
          {holders.length === 0 && (
            <div style={{ marginTop: 4, fontSize: 12, color: "#94a3b8" }}>
              候補がまだいません。すぐ下の「＋ 資格者を追加」で先に登録してください。
            </div>
          )}
        </div>

        <label style={labelStyle}>資格名（肩書）</label>
        <input style={inputStyle} value={qualificationLabel} onChange={(event) => setOverride("head.label", event.target.value)} />

        <label style={labelStyle}>適格請求書発行事業者番号</label>
        <div>
          <input
            style={inputStyle}
            disabled
            placeholder={selectedHolder ? undefined : "先に既定の資格者を選択してください"}
            value={selectedHolder?.invoice_registration_number ?? ""}
          />
          <div style={{ marginTop: 4, fontSize: 12, color: "#64748b" }}>
            この番号は下の資格者一覧の「編集」で変更してください（資格者本人の情報のため、ここでは編集できません）。
          </div>
          {warnInvoiceReg && (
            <div style={warnStyle}>
              {selectedHolder
                ? "テンプレートが参照していますが未入力です。PDFには印字されません。"
                : "テンプレートが適格請求書発行事業者番号を参照しています。既定の資格者を選択してください。"}
            </div>
          )}
        </div>

        <label style={labelStyle}>登録番号</label>
        <div>
          <input style={inputStyle} placeholder="例: 愛知県第123号" value={regValue} onChange={(event) => setOverride("reg", event.target.value)} />
          {warnReg && <div style={warnStyle}>テンプレートで参照されていますが未入力です。PDFには印字されません。</div>}
        </div>

        {showBiz && (
          <>
            <label style={labelStyle}>業務表示</label>
            <div>
              <input style={inputStyle} placeholder="例: 登記・測量" value={bizValue} onChange={(event) => setOverride("biz", event.target.value)} />
              {warnBiz && <div style={warnStyle}>テンプレートで参照されていますが未入力です。PDFには印字されません。</div>}
            </div>
          </>
        )}
      </div>

      {/* 資格者名簿は「既定の資格者」ドロップダウンの直下に置く（設計書 §8.2-4）。
          候補を増やす操作がドロップダウンのすぐ近くにあることが重要。 */}
      {roster}

      <div style={{ padding: "0 16px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>追加項目</div>
          <Button onClick={addItem}>+ 項目を追加</Button>
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {customRules.map((rule) => {
            const warn = refs.has(`rules.${rule.field_key}`) && !ruleValue(rule).trim();
            return (
              <div key={rule.id} style={itemBoxStyle}>
                <div style={{ display: "grid", gridTemplateColumns: "170px 1fr auto", gap: 8, alignItems: "center" }}>
                  <input
                    style={inputStyle}
                    disabled={rule.is_builtin}
                    placeholder="ラベル"
                    value={ruleLabel(rule)}
                    onChange={(event) => setOverride(`rule.${rule.id}.label`, event.target.value)}
                  />
                  <input
                    style={inputStyle}
                    placeholder="値"
                    value={ruleValue(rule)}
                    onChange={(event) => setOverride(`rule.${rule.id}.value`, event.target.value)}
                  />
                  <Button variant="danger" disabled={saving} onClick={() => handleDeleteRule(rule)}>削除</Button>
                </div>
                <div style={keyHintStyle}>テンプレート参照キー: issuer.rules.{rule.field_key}</div>
                {warn && <div style={warnStyle}>テンプレートで参照されていますが未入力です。PDFには印字されません。</div>}
              </div>
            );
          })}

          {newItems.map((item, index) => (
            <div key={`new_${item.field_key}_${index}`} style={{ ...itemBoxStyle, borderStyle: "dashed" }}>
              <div style={{ display: "grid", gridTemplateColumns: "170px 1fr auto", gap: 8, alignItems: "center" }}>
                <input
                  style={inputStyle}
                  placeholder="ラベル（例: 事務所支部）"
                  value={item.label}
                  onChange={(event) => updateNewItem(index, { label: event.target.value })}
                />
                <input
                  style={inputStyle}
                  placeholder="値"
                  value={item.value}
                  onChange={(event) => updateNewItem(index, { value: event.target.value })}
                />
                {item.fromTemplate ? (
                  <span style={{ ...keyHintStyle, color: "#c2410c", whiteSpace: "nowrap" }}>テンプレートが参照</span>
                ) : (
                  <Button onClick={() => setNewItems((current) => current.filter((_, i) => i !== index))}>取消</Button>
                )}
              </div>
              <div style={keyHintStyle}>テンプレート参照キー: issuer.rules.{item.field_key || "（未定）"}</div>
            </div>
          ))}

          {customRules.length === 0 && newItems.length === 0 && (
            <div style={{ color: "#94a3b8", fontSize: 12, padding: "4px 2px" }}>
              追加項目はありません。登録番号以外に印字したい情報がある場合に追加します。
            </div>
          )}
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: "#94a3b8" }}>
          PDF上のどこに・どのように印字するかは、各テンプレートの編集画面で設定します。
        </div>
      </div>
      {children}
    </section>
  );
}

const inputStyle: React.CSSProperties = {
  height: 34,
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "0 10px",
  fontSize: 13,
  fontFamily: "inherit",
  background: "#fff",
  boxSizing: "border-box",
  width: "100%",
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#64748b",
  fontWeight: 800,
  paddingTop: 10,
};

const warnStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: "#c2410c",
  fontWeight: 700,
};

const itemBoxStyle: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: 10,
  background: "#f8fafc",
};

const keyHintStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 11,
  color: "#94a3b8",
  fontFamily: "monospace",
};
